import { useEffect, useMemo, useState } from "react";
import { api } from "../../../lib/api";
import { useCurrentCompany } from "../components/CompanySwitcher";
import { fmtMoney } from "../../../lib/format";

interface LogRow {
  id: number;
  client_id: number;
  client_name: string;
  to_email: string;
  language: "en" | "th";
  subject: string;
  body_preview: string | null;
  total_amount: number;
  status: "sent" | "failed";
  provider_id: string | null;
  error: string | null;
  sent_at: string;
}

type Filter = "all" | "sent" | "failed";

export default function SendLog() {
  const { current } = useCurrentCompany();
  const [log, setLog] = useState<LogRow[]>([]);
  const [filter, setFilter] = useState<Filter>("all");
  const [previewing, setPreviewing] = useState<LogRow | null>(null);

  useEffect(() => {
    if (!current) return;
    api<{ log: LogRow[] }>(`/api/collection/sendlog?company_id=${current.id}&limit=200`)
      .then((r) => setLog(r.log));
  }, [current?.id]);

  const counts = useMemo(() => {
    let sent = 0, failed = 0;
    for (const r of log) (r.status === "sent" ? sent++ : failed++);
    return { sent, failed };
  }, [log]);

  const visible = log.filter((r) => filter === "all" ? true : r.status === filter);
  const currency = current?.currency || "THB";

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tightish text-slate-900">Send log</h1>
          <p className="mt-1 text-sm text-slate-500">
            {log.length} email{log.length !== 1 ? "s" : ""} ·
            <span className="ml-1 text-emerald-700">{counts.sent} sent</span>
            {counts.failed > 0 && <> · <span className="text-rose-700">{counts.failed} failed</span></>}
          </p>
        </div>
        <div className="inline-flex rounded-lg bg-white p-1 shadow-soft ring-1 ring-slate-900/5">
          {(["all", "sent", "failed"] as const).map((k) => (
            <button key={k} onClick={() => setFilter(k)}
              className={`!px-3 !py-1 text-xs ${
                filter === k
                  ? "bg-slate-900 text-white shadow-soft"
                  : "text-slate-600 hover:text-slate-900"
              }`}>
              {k === "all" ? "All" : k === "sent" ? "Sent" : "Failed"}
            </button>
          ))}
        </div>
      </div>

      <div className="card overflow-hidden">
        <table className="refined">
          <thead>
            <tr>
              <th className="w-44">When</th>
              <th>Client</th>
              <th>To</th>
              <th className="w-16">Lang</th>
              <th>Subject</th>
              <th className="w-32 text-right">Total</th>
              <th className="w-28">Status</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.id} className="align-top">
                <td className="text-xs text-slate-500">
                  <div className="font-medium text-slate-700">{fmtWhen(r.sent_at)}</div>
                  <div className="text-[10px] text-slate-400">{fmtTime(r.sent_at)}</div>
                </td>
                <td><div className="font-medium text-slate-900">{r.client_name}</div></td>
                <td className="font-mono text-xs text-slate-600">{r.to_email}</td>
                <td><span className="pill pill-slate uppercase">{r.language}</span></td>
                <td className="max-w-md">
                  <div className="truncate text-slate-700">{r.subject}</div>
                  {r.error && <div className="mt-0.5 truncate text-[11px] text-rose-600">{r.error}</div>}
                </td>
                <td className="text-right font-mono font-semibold text-slate-900">{fmtMoney(r.total_amount, currency)}</td>
                <td>
                  {r.status === "sent"
                    ? <span className="pill pill-green">sent</span>
                    : <span className="pill pill-rose">failed</span>}
                </td>
                <td className="text-right">
                  <button className="btn-ghost !p-1.5" onClick={() => setPreviewing(r)} title="View detail">
                    <svg className="h-4 w-4 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M10 12.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" />
                      <path fillRule="evenodd" d="M.664 10.59a1.651 1.651 0 010-1.186A10.004 10.004 0 0110 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0110 17c-4.257 0-7.893-2.66-9.336-6.41zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
            {!visible.length && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                {log.length
                  ? `No ${filter} messages.`
                  : <><div className="font-medium">No emails sent yet.</div>
                     <div className="text-xs text-slate-400">Pick clients on the Send tab to send your first reminder.</div></>}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {previewing && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm"
             onClick={() => setPreviewing(null)}>
          <div className="card w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">Email detail</div>
                <div className="mt-1 font-semibold text-slate-900">{previewing.client_name}</div>
                <div className="text-xs text-slate-500">{fmtWhen(previewing.sent_at)} · {fmtTime(previewing.sent_at)}</div>
              </div>
              {previewing.status === "sent"
                ? <span className="pill pill-green">sent</span>
                : <span className="pill pill-rose">failed</span>}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
              <Meta label="To" value={previewing.to_email} mono />
              <Meta label="Language" value={previewing.language.toUpperCase()} />
              <Meta label="Total" value={fmtMoney(previewing.total_amount, currency)} mono />
              <Meta label="Provider id" value={previewing.provider_id || "—"} mono />
            </div>

            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Subject</div>
              <div className="mt-0.5 text-sm font-medium text-slate-900">{previewing.subject}</div>
            </div>

            {previewing.error && (
              <div className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-700 ring-1 ring-rose-100">
                <div className="font-semibold">Error</div>
                <div className="mt-0.5">{previewing.error}</div>
              </div>
            )}

            {previewing.body_preview && (
              <div className="mt-4">
                <div className="text-[11px] uppercase tracking-wider text-slate-500">Body preview</div>
                <div className="mt-2 rounded-md border border-slate-200 p-4 text-sm"
                     dangerouslySetInnerHTML={{ __html: previewing.body_preview }} />
                <div className="mt-1 text-[10px] text-slate-400">(first ~500 characters — full body was sent to recipient)</div>
              </div>
            )}

            <div className="mt-5 text-right">
              <button className="btn-secondary" onClick={() => setPreviewing(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Meta({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-md bg-slate-50 p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-0.5 text-slate-900 ${mono ? "font-mono text-[11px]" : "font-medium"}`}>{value}</div>
    </div>
  );
}

function fmtWhen(iso: string): string {
  const d = new Date(iso.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}
function fmtTime(iso: string): string {
  const d = new Date(iso.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}
